import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDownUp, Search } from 'lucide-react';
import Button from './Button';
import CommentCard from './CommentCard';
import EmptyState from './EmptyState';
import Icon from './Icon';
import Tabs from './Tabs';

const SORTS = { time: 'Timecode', newest: 'Newest', oldest: 'Oldest' };
const NEXT = { time: 'newest', newest: 'oldest', oldest: 'time' };

// §2.6 Review set — Comments column (fixed 400px on desktop): Comments / Details tabs, filter row
// (count · sort · search), numbered comment cards, Composer pinned to the bottom.
// comments: [{ id, author: { name, initials }, date, at, text, resolved }]; the number on a card is its place in this array.
// HD-8: cardProps(c) adds props to one card (editor, actions, children, onReply: see CommentCard); the active card
// scrolls into view; sort cycles timecode, newest, oldest; emptyMessage replaces the empty-list sentence.
export default function CommentsColumn({ comments = [], activeId, onSeek, onReply, cardProps, details, composer, format = 'mss', fps, emptyMessage }) {
  const [tab, setTab] = useState('comments');
  const [sort, setSort] = useState('time');
  const [q, setQ] = useState('');
  const list = useRef(null);
  const shown = useMemo(() => {
    const k = q.trim().toLowerCase();
    const xs = comments.map((c, i) => ({ ...c, n: i + 1 })).filter(c => !k || c.text.toLowerCase().includes(k) || (c.author?.name || '').toLowerCase().includes(k));
    if (sort === 'time') return xs.sort((a, b) => (a.at ?? 0) - (b.at ?? 0));
    const d = c => new Date(c.date).getTime();
    return xs.sort((a, b) => (sort === 'newest' ? d(b) - d(a) : d(a) - d(b)));
  }, [comments, sort, q]);
  useEffect(() => {
    if (!activeId || !list.current) return;
    [...list.current.querySelectorAll('[data-comment]')].find(el => el.dataset.comment === String(activeId))?.scrollIntoView({ block: 'nearest' });
  }, [activeId, tab]);
  return (
    <aside className="ui ui-comments" aria-label="Comments">
      <Tabs label="Review panel" value={tab} onChange={setTab} items={[{ id: 'comments', label: 'Comments', count: comments.length }, { id: 'details', label: 'Details' }]} />
      {tab === 'comments' ? (
        <>
          <div className="ui-comments__filter">
            <span className="ui-comments__count">{shown.length === comments.length ? `${comments.length} comments` : `${shown.length} of ${comments.length}`}</span>
            <Button variant="quiet" icon={ArrowDownUp} onClick={() => setSort(s => NEXT[s])} aria-label={`Sorted by ${SORTS[sort].toLowerCase()}, change`}>
              {SORTS[sort]}
            </Button>
            <span className="ui-input-wrap"><Icon icon={Search} /><input className="ui-input" placeholder="Filter" aria-label="Filter comments" value={q} onChange={e => setQ(e.target.value)} /></span>
          </div>
          <div className="ui-comments__list" ref={list}>
            {comments.length === 0 && <EmptyState message={emptyMessage || 'No comments yet. Play the video, pause where something needs a change, and type it below.'} />}
            {comments.length > 0 && shown.length === 0 && <EmptyState message="No comment matches that filter. Clear it to see all of them." action={{ label: 'Clear filter', onClick: () => setQ('') }} />}
            {shown.map(c => (
              <CommentCard key={c.id} data-comment={c.id} index={c.n} author={c.author} date={c.date} at={c.at} text={c.text} resolved={c.resolved}
                active={c.id === activeId} onSeek={t => onSeek?.(t, c)} onReply={onReply ? () => onReply(c) : undefined} format={format} fps={fps}
                {...(cardProps ? cardProps(c) : null)} />
            ))}
          </div>
          {composer}
        </>
      ) : <div className="ui-comments__details">{details}</div>}
    </aside>
  );
}
